import React from 'react';
import { motion } from 'framer-motion';
import Header from '../../layout/Header';
import Footer from '../../sections/Footer';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const { 
  FiHeart, FiAlertTriangle, FiShield, FiArrowLeft,
  FiCheck, FiX, FiMessageSquare, FiDollarSign, FiImage,
  FiVideo, FiGlobe, FiPhoneCall, FiUser, FiBitcoin
} = FiIcons;

const RomanceScamsPage = () => {
  // Common tactics used in romance scams
  const commonTactics = [
    "Creating fake profiles with stolen photos and made-up life stories",
    "Quickly professing deep feelings or talking about marriage early in the relationship",
    "Making excuses for why they can't video chat or meet in person",
    "Claiming to work overseas (military, oil rig, international business, doctor in conflict zone)",
    "Sharing elaborate stories of hardship that require financial assistance",
    "Promising to visit but canceling due to emergencies requiring money",
    "Moving conversations off dating sites to private messaging platforms"
  ];

  // Protection tips
  const protectionTips = [
    "Research the person's profile picture using reverse image search tools",
    "Take relationships slowly and be suspicious of anyone who tries to rush intimacy",
    "Never send money to someone you haven't met in person",
    "Be wary if they refuse video calls or make excuses not to meet",
    "Discuss the relationship with trusted friends or family members",
    "Watch for poor grammar or inconsistencies in their stories",
    "Be skeptical of someone who seems \"too perfect\" or whose messages seem scripted"
  ];

  // Victim recovery steps
  const recoverySteps = [
    "Stop all communication with the scammer immediately",
    "Document all interactions, including messages, emails, and payment details",
    "Report the scam to the dating site or app where you met the person",
    "File reports with the FBI's Internet Crime Complaint Center (IC3) and the FTC",
    "Contact your financial institution if you've sent money",
    "Report the scam to local law enforcement",
    "Seek emotional support through counseling or support groups for scam victims"
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1577495508048-b635879837f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
            alt="Romance Scam Concept"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-purple-900/80 to-gray-900/90"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link 
              to="/types-of-scams" 
              className="inline-flex items-center text-gray-400 hover:text-[#ffc700] mb-6 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Back to All Scam Types
            </Link>

            <div className="flex items-center gap-4 mb-4">
              <div className="bg-gradient-to-br from-pink-500 to-red-500 p-4 rounded-full">
                <SafeIcon icon={FiHeart} className="text-2xl text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
                Romance Scams
              </h1>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
              <SafeIcon icon={FiAlertTriangle} className="text-red-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-400">High-Risk Scam Alert</h3>
                <p className="text-gray-300">
                  Romance scams caused over $1.3 billion in reported losses in 2023, with the average victim losing $16,000.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-16">
            {/* Overview Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
                  What Are Romance Scams?
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Romance scams occur when criminals create fake identities to build online relationships with victims, ultimately manipulating them into sending money or sharing personal information. These scammers are masters of emotional manipulation, spending weeks or months gaining your trust before revealing their true intentions.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Using dating apps, social media, or specialized dating websites, scammers target people looking for companionship, often claiming to be working abroad as military personnel, oil rig workers, international businesspeople, or doctors in conflict zones. They craft compelling backstories designed to explain their inability to meet in person while creating emotional connections that cloud victims' judgment.
                </p>
                
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-pink-500 mb-2">78%</div>
                    <p className="text-gray-400">of victims are women over 40</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-pink-500 mb-2">6-8</div>
                    <p className="text-gray-400">months average scam duration</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="text-4xl font-bold text-pink-500 mb-2">$16K</div>
                    <p className="text-gray-400">average financial loss</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* How It Works Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
                  How Romance Scams Work
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="space-y-10">
                  {/* Step 1 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-pink-500/20 p-4 rounded-full border border-pink-500/30 flex-shrink-0">
                      <SafeIcon icon={FiImage} className="text-2xl text-pink-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Creating the Perfect Profile</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Scammers create compelling profiles using stolen photos and fabricated life stories. They often pose as successful professionals with international careers or military personnel deployed overseas. Their profiles are designed to seem trustworthy and appealing to their target demographic.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-pink-500/20 p-4 rounded-full border border-pink-500/30 flex-shrink-0">
                      <SafeIcon icon={FiMessageSquare} className="text-2xl text-pink-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Building Intense Emotional Connection</h3>
                      <p className="text-gray-300 leading-relaxed">
                        Once contact is established, scammers move quickly to create deep emotional bonds. They send frequent messages, show intense interest in your life, and may even send small gifts. They'll profess love unusually early and begin planning a future together, all while avoiding video calls or in-person meetings.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-pink-500/20 p-4 rounded-full border border-pink-500/30 flex-shrink-0">
                      <SafeIcon icon={FiDollarSign} className="text-2xl text-pink-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">The Financial Emergency</h3>
                      <p className="text-gray-300 leading-relaxed">
                        After establishing trust, the scammer introduces a sudden crisis requiring financial help—medical emergencies, legal troubles, travel expenses to finally meet you, or business opportunities they want to share with you. The requests often start small and gradually increase as the victim proves willing to help.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="bg-pink-500/20 p-4 rounded-full border border-pink-500/30 flex-shrink-0">
                      <SafeIcon icon={FiGlobe} className="text-2xl text-pink-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Continued Manipulation</h3>
                      <p className="text-gray-300 leading-relaxed">
                        If you send money, the scammer will invent new emergencies requiring additional funds. If you hesitate, they may use guilt, promises of repayment, or declarations of love to manipulate you. They might even threaten to end the relationship if help isn't provided. This cycle continues until the victim stops sending money or discovers the scam.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10">
                  <h3 className="text-xl font-bold text-white mb-4">Common Tactics & Red Flags</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {commonTactics.map((tactic, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <SafeIcon icon={FiAlertTriangle} className="text-red-500 mt-1 flex-shrink-0" />
                        <p className="text-gray-300">{tactic}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Protection Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
                  How to Protect Yourself
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {protectionTips.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <SafeIcon icon={FiCheck} className="text-green-500 mt-1 flex-shrink-0" />
                      <p className="text-gray-300">{tip}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiVideo} className="text-pink-500" />
                    Linda's Quick Tips for Safe Online Dating
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    "If they won't video chat, there's a reason why. Real people who are interested in real relationships don't hide their faces. And honey, if they're asking for iTunes gift cards to help their sick grandmother, block and delete. Love don't cost a thing... especially not gift cards."
                  </p>
                  <p className="text-[#ffc700] font-medium">- Linda Roberts</p>
                </div>
              </div>
            </motion.div>

            {/* What to Do If You're a Victim */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">
                  What to Do If You're a Victim
                </span>
              </h2>
              <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  If you believe you've been targeted by a romance scammer, don't feel ashamed. These criminals are sophisticated manipulators who know exactly how to exploit emotions. Taking quick action can help minimize damage and potentially help authorities catch the perpetrators.
                </p>

                <div className="space-y-4 mb-8">
                  {recoverySteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="bg-gradient-to-br from-pink-500 to-red-500 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      <p className="text-gray-300">{step}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4">Important Resources</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-pink-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">FBI Internet Crime Complaint Center (IC3)</p>
                        <a href="https://www.ic3.gov" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">www.ic3.gov</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-pink-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">Federal Trade Commission (FTC)</p>
                        <a href="https://reportfraud.ftc.gov" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">reportfraud.ftc.gov</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiPhoneCall} className="text-pink-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">AVAH Outreach Group</p>
                        <Link to="/scam-assistance" className="text-pink-400 hover:underline">Get Free Victim Support</Link>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiGlobe} className="text-pink-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">Romance Scams Now</p>
                        <a href="https://romancescamsnow.com" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">romancescamsnow.com</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next Steps CTA */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-purple-900 to-gray-900 p-8 rounded-2xl border border-pink-500/30 relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/10 rounded-full translate-y-12 -translate-x-12"></div>

              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-4 text-center">
                  Continue Learning About Common Scams
                </h3>
                <p className="text-gray-300 text-center max-w-2xl mx-auto mb-8">
                  Knowledge is your best defense against scammers. Explore these related scam types to better protect yourself and your loved ones.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <Link
                    to="/scams/online-dating-extortion"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-pink-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiHeart} className="text-2xl text-pink-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Online Dating Extortion</p>
                  </Link>
                  <Link
                    to="/scams/impersonation-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-pink-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiUser} className="text-2xl text-pink-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Impersonation Scams</p>
                  </Link>
                  <Link
                    to="/scams/cryptocurrency-scams"
                    className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-pink-500/50 transition-all duration-300 text-center"
                  >
                    <SafeIcon icon={FiBitcoin} className="text-2xl text-pink-500 mx-auto mb-2" />
                    <p className="font-bold text-white">Cryptocurrency Scams</p>
                  </Link>
                </div>

                <div className="mt-8 text-center">
                  <Link
                    to="/types-of-scams"
                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3 shadow-lg"
                  >
                    <SafeIcon icon={FiShield} className="text-xl" />
                    View All Scam Types
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RomanceScamsPage;